import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useStreamWsReconnectTask from '../../utils/useStreamWsReconnectTask';
import type { ReconnectTaskStreamWS } from '../../utils/useStreamWsReconnectTask';
import { backfillAlerts } from './incidentsSlice';

export type AlertsBackfillStreamWS = ReconnectTaskStreamWS;

interface UseAlertsBackfillOnReconnectArgs {
  streamWS: AlertsBackfillStreamWS | null | undefined;
  enabled: boolean;
  orgName: string | undefined;
  queueMaxSize: number | string;
  // The user's application; decides which statuses count as newly arrived.
  currentApplication: string | undefined;
  // Must be unique per app so two registrations on one socket don't collide.
  callbackName?: string;
}

const DEFAULT_CALLBACK_NAME = 'handleAlertsBackfillReconnect';

/**
 * Re-fetches alerts when the websocket-server connection comes back so alerts
 * published while the socket was down are not lost until the next page load.
 * Results are merged into the store, never replacing it, so an alert received
 * over both the websocket and the backfill appears once.
 */
const useAlertsBackfillOnReconnect = ({
  streamWS,
  enabled,
  orgName,
  queueMaxSize,
  currentApplication,
  callbackName = DEFAULT_CALLBACK_NAME,
}: UseAlertsBackfillOnReconnectArgs): void => {
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();

  const run = useCallback(
    () =>
      dispatch(
        backfillAlerts({
          orgName: orgName as string,
          queueMaxSize,
          endTime: Date.now(),
          currentApplication,
        }),
      ).unwrap(),
    [dispatch, orgName, queueMaxSize, currentApplication],
  );

  useStreamWsReconnectTask({
    streamWS,
    enabled: enabled && Boolean(orgName),
    run,
    callbackName,
    sentryTag: 'alerts-backfill',
  });
};

export default useAlertsBackfillOnReconnect;
